// Which program-level changes this viewer may make. `canEdit` is the program's
// `update` alone; the servers also want an area (and sometimes a further
// verb), so a control gated on it alone shows for roles the server refuses —
// the internal Quality Assurance team holds `update` and only area:quality.
// Each flag mirrors its server gate off the resolve the page already makes:
//
//   - contractDocuments: `update` + any of area:programs / area:execution /
//     area:documents / area:contracts — zou's program bridge for uploading and
//     processing mods, SOWs and program documents
//   - roster:        `update` + `area:programs` (companies, staffing, contacts,
//                    capabilities, team seats)
//   - closeoutChecklist: `update` + `area:programs` (start, answer, outstanding
//                    items, complete, export)
//   - execution:     `update` + `area:execution` (optional tasks, exercises,
//                    periods of performance)
//   - quality:       `update` + `area:quality` (Approved Changes / deviations)
//   - riskIssues:    `update` + `area:quality` + `area:execution` — ryugu's
//                    risk/issue edit gate; Quality reads and comments only
//   - recordDocuments: `create` + `area:documents` (Record Document, attach)
//   - deleteDocuments: `update` + `area:documents`
//   - closeProgram:  `update` + `area:programs` + `close` (closeout's Close)
//   - touchpoints:   `update` + `area:growth` (touchpoints are upserts, files attach)
//   - qualityScores: `update` + `area:quality.scores` (record CPARS/QASP scores;
//     Quality Assurance holds this after 001001; Delete is a separate server flag)
//   - contracts:     `update` + `area:contracts` (correct a CLIN line)
//   - comment:       `update` on the program (deliverable comments; no area)
//   - activate:      the activation gate's `activate` is an admin act
//
// Fail-closed: every flag is false while the projection loads.
import { useAuth } from '../../../contexts/authContext';
import { programRef } from '../../../lib/access/types';
import { useView } from '../../../lib/access/useView';
const CONTRACT_DOCUMENT_AREAS = [
    'area:programs',
    'area:execution',
    'area:documents',
    'area:contracts'
];
// Title on a disabled write control: which area grant the server also wants.
// Labels match the admin permissions vocabulary ("Where they can work").
export const PROGRAM_WRITE_DENIED = {
    roster: 'This needs Program setup & roster permission on this program.',
    quality: 'This needs Quality & compliance permission on this program.',
    execution: 'This needs Execution & schedule permission on this program.'
};
export function useProgramWriteAccess(programId) {
    const v = useView(programRef(programId), {
        enabled: !!programId
    });
    const role = useAuth().user?.role?.toLowerCase() ?? '';
    const has = (a)=>v.granted(a);
    const update = has('update');
    return {
        isLoading: v.isLoading,
        contractDocuments: update && CONTRACT_DOCUMENT_AREAS.some(has),
        roster: update && has('area:programs'),
        closeoutChecklist: update && has('area:programs'),
        execution: update && has('area:execution'),
        quality: update && has('area:quality'),
        riskIssues: update && has('area:quality') && has('area:execution'),
        recordDocuments: has('create') && has('area:documents'),
        deleteDocuments: update && has('area:documents'),
        closeProgram: update && has('area:programs') && has('close'),
        touchpoints: update && has('area:growth'),
        qualityScores: update && has('area:quality.scores'),
        contracts: update && has('area:contracts'),
        comment: update,
        activate: role === 'admin' || role === 'internal'
    };
}
